import { useLocation } from 'react-router-dom';
import {
  ButtonPrimary,
  ButtonPseudo,
  SidePanel,
  SidePanelType,
  PermissionEnum,
  Popup,
  IconSelected,
  ChangeLogs,
  PermissionDenied,
} from '@ui/src';
import {
  NotificationCategoryEnum,
  NotificationItem,
} from '@notifications/hoc/center/types';
import NotificationCenterFilters from '@notifications/components/notification-center/filters-sidepanel';
import NotificationCenterMainColumn from '@notifications/components/notification-center/main-column';
import NotificationsControlsPanel from '@notifications/components/notification-center/controls-panel';
import EditNotificationsSidepanel from '@notifications/components/notification-center/edit-sidepanel';
import NotificationCommentColumn from '@notifications/components/notification-center/comment-column';
import NotificationEditColumn from '@notifications/components/notification-center/notification-edit-column';
import { useAppSelector, useAppDispatch } from '@store';
import { setSelectedNotifications, clearChangeLogs } from './store/slices';
import useRequests from './hooks/use-notifications';

import cn from './notification-center.module.css';

const NotificationCenter = () => {
  const { search } = useLocation();

  const { activeUsers, users } = useAppSelector(
    (state) => state.administrationDictionaries,
  );
  const {
    notifications,
    selectedNotifications,
    pagination,
    comments,
    changeLogs,
    changeLogsPagination,
    notificationEntity,
    selectedNotificationId,
    selectedNotification,
  } = useAppSelector((state) => state.customersNotificationsCenter);
  const { user } = useAppSelector((state) => state.auth);
  const { activePermissions } = useAppSelector((state) => state.root);

  const dispatch = useAppDispatch();

  const {
    scrollRef,
    control,
    reset,
    setValue,
    editControl,
    editReset,
    isDirty,
    editIssueControl,
    editIssueIsDirty,
    errors,
    filterFields,
    getChangeLogsAction,
    comment,
    setComment,
    commentError,
    onSendComment,
    nextPageIsLoading,
    onScroll,
    editError,
    onEditIssueSubmit,
    selectedCategories,
    setSelectedCategories,
    selectedGroups,
    setSelectedGroups,
    selectedActions,
    setSelectedActions,
    onCategoryFilterApply,
    deadlineFilter,
    selectedStatuses,
    setSelectedStatuses,
    selectedExecutorId,
    setSelectedExecutorId,
    onApplyStatusFilter,
    onApplyExecutorFilter,
    onApplyDeadlineFilter,
    onClearDeadlineFilter,
    onClearExecutorFilter,
    onClearStatusFilter,
    onCategoryFilterClear,
    panelIsVisible,
    setPanelIsVisible,
    onFiltersSubmit,
    selectedStatus,
    setSelectedStatus,
    changeLogsPanelIsOpen,
    setChangeLogPanelIsOpen,
    selectedDeadline,
    setSelectedDeadline,
    batchUpdateError,
    setBatchUpdateError,
    editSidepanelVisible,
    setEditSidepanelVisible,
    onEditSubmit,
    statusesChangeNotificationVisible,
    deadlineChangeNotificationVisible,
    onEditSubmitFromControlPanel,
    updateIssueCommentAction,
    batchErrors,
    setEditIssueValue,
    editIssueFields,
    massEditIssueFields,
    setMassEditValue,
    addCommentRequestInProgress,
    changeLogsIsLoading,
    changeLogsIsFetching,
    isLoading,
    isFetching,
    updateInProgress,
    commentsIsLoading,
    commentsIsFetching,
    applicationEntityIsLoading,
    applicationEntityIsFetching,
    channelEntityIsLoading,
    channelEntityIsFetching,
    organizationEntityIsLoading,
    organizationEntityIsFetching,
    batchUpdateInProgress,
    batchAddIssueCommentInProgress,
    updateIssueCommentInProgress,
    updateIssueCommentIsSuccess,
    setEditIssueDirtyPopupVisible,
    setPrepareSelectedNotification,
    onChangeNotificationItem,
    categories,
    groups,
    types,
    selectedGroup,
    setSelectedGroup,
    setDirtyPopupVisible,
    prepareSelectedNotification,
    editIssueDirtyPopupVisible,
    dirtyPopupVisible,
  } = useRequests({
    defaultFiltersValue: {
      createdAt: [],
      createdAtFrom: '',
      createdAtTo: '',
      deadline: [],
      deadlineFrom: '',
      deadlineTo: '',
      executorId: [],
      group: [],
      responsibleId: [],
      status: [],
      type: [],
    },
    selectedNotification,
    pagination,
    search,
    changeLogs,
    activePermissions,
    commentsListAvailable: activePermissions?.includes(
      PermissionEnum.NOTIFICATIONS_MESSAGING_AND_CUSTOMERS_COMMENTS_LIST,
    ),
    withoutCategoryFilter: true,
    selectedNotifications,
    setSelectedNotifications: (ids: string[]) =>
      dispatch(setSelectedNotifications(ids)),
    selectedNotificationId,
    activeUsers,
    notifications,
  });

  let content;

  if (
    activePermissions &&
    activePermissions.includes(
      PermissionEnum.NOTIFICATIONS_MESSAGING_AND_CUSTOMERS_LIST,
    )
  ) {
    content = (
      <div className={cn.notificationCenterWrapper}>
        <NotificationCenterMainColumn
          selectedCategories={selectedCategories}
          setSelectedCategories={setSelectedCategories}
          selectedGroups={selectedGroups}
          setSelectedGroups={setSelectedGroups}
          selectedActions={selectedActions}
          setSelectedActions={setSelectedActions}
          setPanelIsVisible={setPanelIsVisible}
          onCategoryFilterApply={onCategoryFilterApply}
          onCategoryFilterClear={onCategoryFilterClear}
          selectedStatuses={selectedStatuses}
          setSelectedStatuses={setSelectedStatuses}
          selectedExecutorId={selectedExecutorId}
          setSelectedExecutorId={setSelectedExecutorId}
          employees={activeUsers}
          notifications={notifications}
          selectedNotifications={selectedNotifications}
          selectedNotificationId={selectedNotification?.id}
          setSelectedNotification={(data) => {
            if (editIssueIsDirty) {
              setEditIssueDirtyPopupVisible(true);
              setPrepareSelectedNotification(data);
            } else {
              onChangeNotificationItem(data);
            }
          }}
          categories={categories}
          groups={groups}
          types={types}
          selectedCategory={NotificationCategoryEnum.CUSTOMERS_AND_MESSAGING}
          selectedGroup={selectedGroup}
          setSelectedGroup={setSelectedGroup}
          setSelectedNotifications={(notificationsList: string[]) =>
            dispatch(setSelectedNotifications(notificationsList))
          }
          onApplyStatusFilter={onApplyStatusFilter}
          onClearStatusFilter={onClearStatusFilter}
          onApplyExecutorFilter={onApplyExecutorFilter}
          onClearExecutorFilter={onClearExecutorFilter}
          deadlineFilter={deadlineFilter}
          setDeadlineFilter={onApplyDeadlineFilter}
          onClearDeadlineFilter={onClearDeadlineFilter}
          isLoading={isLoading}
          isFetching={isFetching}
          filterFields={filterFields}
          scrollRef={scrollRef}
          onScroll={onScroll}
          nextPageIsLoading={nextPageIsLoading}
          categoryFilterTitle="Group"
          withoutCategoryBackButton
        />
        <NotificationCommentColumn
          notification={selectedNotification}
          comment={comment}
          setComment={setComment}
          commentAvailable={activePermissions?.includes(
            PermissionEnum.NOTIFICATIONS_MESSAGING_AND_CUSTOMERS_COMMENTS_CREATE_ACTION,
          )}
          editCommentsAvailable={activePermissions?.includes(
            PermissionEnum.NOTIFICATIONS_MESSAGING_AND_CUSTOMERS_COMMENTS_EDIT_ACTION,
          )}
          commentsListAvailable={activePermissions?.includes(
            PermissionEnum.NOTIFICATIONS_MESSAGING_AND_CUSTOMERS_COMMENTS_LIST,
          )}
          onSendComment={onSendComment}
          requestInProgress={addCommentRequestInProgress}
          commentsIsLoading={commentsIsLoading || commentsIsFetching}
          commentError={commentError}
          comments={comments}
          notificationEntityIsLoading={
            applicationEntityIsLoading ||
            applicationEntityIsFetching ||
            channelEntityIsLoading ||
            channelEntityIsFetching ||
            organizationEntityIsLoading ||
            organizationEntityIsFetching
          }
          notificationEntity={notificationEntity}
          updateIssueCommentAction={updateIssueCommentAction}
          updateIssueCommentInProgress={updateIssueCommentInProgress}
          updateIssueCommentIsSuccess={updateIssueCommentIsSuccess}
        />
        <NotificationEditColumn
          notification={selectedNotification}
          employees={activeUsers}
          setChangeLogPanelIsOpen={setChangeLogPanelIsOpen}
          auditLogAvailable={activePermissions?.includes(
            PermissionEnum.AUDIT_LOG_LIST,
          )}
          editIssueControl={editIssueControl}
          isDirty={editIssueIsDirty}
          onEditIssueSubmit={onEditIssueSubmit}
          editError={editError}
          updateInProgress={updateInProgress}
          errors={errors}
          editAvailable={activePermissions.includes(
            PermissionEnum.NOTIFICATIONS_MESSAGING_AND_CUSTOMERS_EDIT_ACTION,
          )}
          setEditIssueValue={setEditIssueValue}
          currentUser={user}
          editIssueFields={editIssueFields}
        />
        <SidePanel
          type={SidePanelType.Accounts}
          isOpen={panelIsVisible}
          title="Filters"
          sidepanelExpandedKey="notification-center-filters"
          onClose={() => {
            setPanelIsVisible(false);

            if (selectedGroups?.length && !filterFields.group?.length) {
              setValue('group', selectedGroups);
            }

            if (selectedActions?.length && !filterFields?.type?.length) {
              setValue('type', selectedActions);
            }
          }}
          footer={
            <div className={cn.filtersSidepanelFooter}>
              <ButtonPrimary
                text="Apply filters"
                onClick={onFiltersSubmit}
                bold
              />
              <ButtonPseudo
                text="Clear all"
                onClick={() =>
                  reset({
                    group: [],
                    type: [],
                    status: [],
                    responsibleId: [],
                    executorId: [],
                    createdAt: [],
                    createdAtFrom: '',
                    createdAtTo: '',
                    deadline: [],
                    deadlineFrom: '',
                    deadlineTo: '',
                  })
                }
                bold
              />
            </div>
          }
          closeOnClickOutside
        >
          <NotificationCenterFilters
            control={control}
            users={activeUsers}
            setValue={setValue}
            onReset={() => {
              setValue('deadlineFrom', '');
              setValue('deadlineTo', '');
              setValue('deadline', []);
            }}
            filterFields={filterFields}
            category={NotificationCategoryEnum.CUSTOMERS_AND_MESSAGING}
            currentUser={user}
            withoutCategories
          />
        </SidePanel>
        <SidePanel
          type={SidePanelType.Accounts}
          isOpen={editSidepanelVisible}
          title="Mass edit"
          sidepanelExpandedKey="notification-center-mass-edit"
          onClose={() => {
            if (isDirty) {
              setDirtyPopupVisible(true);
            } else {
              setEditSidepanelVisible(false);
              editReset({});
            }
          }}
          errorSidepanel={batchUpdateError}
          footer={
            <>
              <ButtonPrimary
                text={
                  batchUpdateInProgress || batchAddIssueCommentInProgress
                    ? 'Loading...'
                    : 'Save tasks'
                }
                onClick={onEditSubmit}
                disabled={
                  batchUpdateInProgress ||
                  batchAddIssueCommentInProgress ||
                  !isDirty
                }
                bold
              />
              <ButtonPseudo
                text="Close"
                onClick={() => {
                  if (isDirty) {
                    setDirtyPopupVisible(true);
                  } else {
                    setEditSidepanelVisible(false);
                    editReset({
                      status: '',
                      responsibleId: '',
                      executorId: '',
                      deadline: '',
                      comment: '',
                      ids: [],
                    });
                    setBatchUpdateError('');
                  }
                }}
                bold
              />
            </>
          }
          closeOnClickOutside
        >
          <EditNotificationsSidepanel
            selectedNotifications={selectedNotifications}
            control={editControl}
            employees={activeUsers}
            batchUpdateInProgress={
              batchUpdateInProgress || batchAddIssueCommentInProgress
            }
            errors={batchErrors}
            currentUser={user}
            editIssueFields={massEditIssueFields}
            setValue={setMassEditValue}
          />
        </SidePanel>
        <SidePanel
          isOpen={changeLogsPanelIsOpen}
          onClose={() => {
            setChangeLogPanelIsOpen(false);
            dispatch(clearChangeLogs());
          }}
          title="Notification issue change logs"
          sidepanelExpandedKey="notification-center-issue-change-logs"
          type={SidePanelType.Accounts}
          footer={
            <div>
              <ButtonPseudo
                text="Close"
                onClick={() => setChangeLogPanelIsOpen(false)}
                bold
              />
            </div>
          }
          closeOnClickOutside
        >
          <>
            {activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST) && (
              <ChangeLogs
                entityType={['ISSUE', 'ISSUE_COMMENT']}
                entityId={selectedNotification?.issue?.id as string}
                changeLogsIsLoading={changeLogsIsLoading}
                changeLogs={changeLogs}
                activePermission={activePermissions}
                users={users}
                pagination={changeLogsPagination}
                changeLogsIsFetching={changeLogsIsFetching}
                getChangeLogs={getChangeLogsAction}
              />
            )}
            {!activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST) && (
              <PermissionDenied />
            )}
          </>
        </SidePanel>
        <Popup
          title="You have unsaved changes"
          description=""
          onCancelClick={() => setEditIssueDirtyPopupVisible(false)}
          onOkClick={() => {
            setEditIssueDirtyPopupVisible(false);

            onChangeNotificationItem(
              prepareSelectedNotification as NotificationItem,
            );
          }}
          cancelButtonText="Cancel"
          okButtonText="Close"
          isVisible={editIssueDirtyPopupVisible}
          parent={document.getElementById('dirty-popup') as HTMLElement}
        />
        <Popup
          title="You have unsaved changes"
          description=""
          onCancelClick={() => setDirtyPopupVisible(false)}
          onOkClick={() => {
            setDirtyPopupVisible(false);
            dispatch(setSelectedNotifications([]));
            setEditSidepanelVisible(false);
            editReset({
              status: '',
              responsibleId: '',
              executorId: '',
              deadline: '',
              comment: '',
              ids: [],
            });
            setBatchUpdateError('');
          }}
          cancelButtonText="Cancel"
          okButtonText="Close"
          isVisible={dirtyPopupVisible}
          parent={document.getElementById('dirty-popup') as HTMLElement}
        />
        {statusesChangeNotificationVisible && (
          <div className={cn.statusChangeNotification}>
            <div className={cn.statusChangeNotificationContent}>
              <IconSelected />
              {`Status changed to ${selectedStatus}`}
            </div>
          </div>
        )}
        {deadlineChangeNotificationVisible && (
          <div className={cn.statusChangeNotification}>
            <div className={cn.statusChangeNotificationContent}>
              <IconSelected />
              {`Deadline changed to ${new Date(
                selectedDeadline,
              ).toLocaleDateString()}`}
            </div>
          </div>
        )}
        {selectedNotifications &&
          selectedNotifications?.length > 0 &&
          !editSidepanelVisible && (
            <NotificationsControlsPanel
              selectedNotifications={selectedNotifications}
              notificationsLength={notifications?.length}
              onSelectedAll={() => {
                if (selectedNotifications?.length !== notifications?.length) {
                  dispatch(
                    setSelectedNotifications(
                      notifications?.map((item) => item.id) as string[],
                    ),
                  );
                } else {
                  dispatch(setSelectedNotifications([]));
                }
              }}
              setEditSidepanelVisible={setEditSidepanelVisible}
              setSelectedStatus={setSelectedStatus}
              setSelectedDeadline={setSelectedDeadline}
              onClose={() => dispatch(setSelectedNotifications([]))}
              onSubmit={onEditSubmitFromControlPanel}
            />
          )}
        <div id="dirty-popup" />
      </div>
    );
  } else if (
    activePermissions &&
    !activePermissions?.includes(
      PermissionEnum.NOTIFICATIONS_MESSAGING_AND_CUSTOMERS_LIST,
    )
  ) {
    content = <PermissionDenied />;
  }

  return content;
};

export default NotificationCenter;
